import React, { useState } from 'react';
import { Skeleton } from '@components/atoms/Skeleton';
import ErrorMessage from '@components/molecules/ErrorMessage';
import { EvidenceSummaryBar } from './EvidenceSummaryBar';
import { EvidenceSlotCard } from './EvidenceSlotCard';
import { EvidenceCaptureLane } from './EvidenceCaptureLane';
import { EvidenceLightbox } from './EvidenceLightbox';
import { useEvidenceSlotsForTask, useEvidenceSlotsForProject } from '@hooks/api';
import { EvidenceCaptureRef, EvidenceSlotState } from '@services/evidenceSlot';

interface EvidenceGridProps {
  taskId?: string;
  projectId?: string;
}

/**
 * Sprint 11, story-042 (U77). Fixed-cell grid (per story-041's slot catalog) + the
 * PROGRESS_PHOTO repeatable lane, read-only this sprint. Renders story-041's slot-fulfillment
 * result — never computes fulfillment client-side, per docs/ux/story-042-design.md's explicit
 * "no platform re-derives fulfillment" rule.
 */
export const EvidenceGrid: React.FC<EvidenceGridProps> = ({ taskId, projectId }) => {
  const taskQuery = useEvidenceSlotsForTask(taskId);
  const projectQuery = useEvidenceSlotsForProject(!taskId ? projectId : undefined);
  const { data: slots, isLoading, isError } = taskId ? taskQuery : projectQuery;
  const [lightboxCapture, setLightboxCapture] = useState<EvidenceCaptureRef | null>(null);

  const openLightbox = (slotList: EvidenceSlotState[], documentId: string) => {
    const found = slotList.flatMap((s) => s.captures).find((c) => c.documentId === documentId);
    if (found) setLightboxCapture(found);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="evidence-grid-loading">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} height={180} rounded="lg" />
        ))}
      </div>
    );
  }

  if (isError || !slots) {
    return <ErrorMessage message="Couldn't load evidence — please try again." />;
  }

  const nonRepeatableSlots = slots.filter((s) => !s.repeatable);
  const repeatableSlots = slots.filter((s) => s.repeatable);
  const requiredSlots = slots.filter((s) => s.required);
  const requiredFilled = requiredSlots.filter((s) => s.status === 'FILLED').length;

  return (
    <div data-testid="evidence-grid" className="flex flex-col gap-4">
      <EvidenceSummaryBar requiredTotal={requiredSlots.length} requiredFilled={requiredFilled} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {nonRepeatableSlots.map((slot) => (
          <EvidenceSlotCard key={slot.slotId} slot={slot} onOpenLightbox={(id) => openLightbox(slots, id)} />
        ))}
      </div>

      {repeatableSlots.map((slot) => (
        <div key={slot.slotId} className="flex flex-col gap-2">
          <h4 className="text-sm font-medium">
            {slot.label} ({slot.captures.length})
          </h4>
          <EvidenceCaptureLane captures={slot.captures} onOpenLightbox={(id) => openLightbox(slots, id)} />
        </div>
      ))}

      <EvidenceLightbox capture={lightboxCapture} onClose={() => setLightboxCapture(null)} />
    </div>
  );
};

export default EvidenceGrid;
